/**
 * Variable products on the portal product page (after-me/product-detail).
 *
 * Matches the attribute selects against the variation data WooCommerce puts on
 * the form (data-product_variations), fills input.variation_id for the
 * add-to-cart module, and mirrors the chosen variation into the page — price,
 * stock badge, SKU row, image and quantity limits — restoring them when the
 * selection is cleared. Options that can't combine with the other choices are
 * disabled.
 *
 * Products with more variations than woocommerce_ajax_variation_threshold ship
 * data-product_variations="false"; those are looked up through WooCommerce's
 * ?wc-ajax=get_variation endpoint instead, once every attribute is chosen.
 */
import { syncStepper } from "./stepper.js";

const TEXT = {
	noMatch: "No product matches that combination. Please choose different options.",
	unavailable: "Sorry, this option is unavailable.",
	outOfStock: "Out of stock",
	inStock: "In stock",
};

function units(count, unit) {
	if (!unit) return String(count);
	return `${count} ${count === 1 || unit.endsWith("s") ? unit : `${unit}s`}`;
}

function stockBadge(variation, unit, low) {
	if (!variation.is_in_stock) return [TEXT.outOfStock, "amp-badge amp-badge--out"];

	// max_qty is the stock count when the variation manages stock, "" otherwise.
	const stock = Number(variation.max_qty);
	if (variation.max_qty !== "" && Number.isFinite(stock)) {
		return [`In stock: ${units(stock, unit)}`, `amp-badge${stock <= low ? " amp-badge--warn" : ""}`];
	}
	return [TEXT.inStock, "amp-badge"];
}

function variationImage(variation) {
	const image = variation.image || {};
	if (!variation.image_id || !image.src) return null;

	const img = document.createElement("img");
	img.src = image.src;
	if (image.srcset) img.srcset = image.srcset;
	if (image.sizes) img.sizes = image.sizes;
	img.alt = image.alt || "";
	if (image.src_w) img.width = image.src_w;
	if (image.src_h) img.height = image.src_h;
	return img;
}

/** A variation matches when each of its attributes is "any" ("") or equal to the choice. */
function matches(variation, chosen) {
	return Object.entries(variation.attributes || {}).every(
		([name, value]) => value === "" || chosen[name] === undefined || chosen[name] === "" || value === chosen[name]
	);
}

function parseVariations(form) {
	const raw = form.dataset.product_variations;
	if (!raw || raw === "false") return null;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function bind(form) {
	const root = form.closest(".amp-product") || document;
	const unit = root.dataset.ampUnit || "";
	const low = Number(root.dataset.ampLow) || 0;
	const variations = parseVariations(form);

	const selects = [...form.querySelectorAll('select[name^="attribute_"]')];
	const idInput = form.querySelector("input.variation_id");
	const submit = form.querySelector("[data-amp-variation-submit]");
	const resetBtn = form.querySelector("[data-amp-variation-reset]");
	const note = form.querySelector("[data-amp-variation-note]");
	const stepper = form.querySelector("[data-amp-stepper]");
	const qty = stepper?.querySelector("input");

	const price = root.querySelector("[data-amp-price]");
	const badge = root.querySelector("[data-amp-stock]");
	const sku = root.querySelector("[data-amp-sku]");
	const media = root.querySelector("[data-amp-media]");

	const original = {
		price: price?.innerHTML,
		badge: badge && [badge.textContent, badge.className],
		sku: sku?.textContent,
		media: media?.innerHTML,
		min: qty?.getAttribute("min"),
		max: qty?.getAttribute("max"),
	};

	let request = 0;

	const chosen = () => Object.fromEntries(selects.map((s) => [s.name, s.value]));

	const setNote = (html, tone = "") => {
		if (!note) return;
		note.innerHTML = html;
		note.hidden = html === "";
		note.dataset.tone = tone;
	};

	const setQtyLimits = (min, max) => {
		if (!qty) return;
		qty.setAttribute("min", min);
		if (max === "" || max === null || max === undefined) qty.removeAttribute("max");
		else qty.setAttribute("max", max);
		syncStepper(stepper);
	};

	const restore = () => {
		if (idInput) idInput.value = "0";
		if (price) price.innerHTML = original.price;
		if (badge) [badge.textContent, badge.className] = original.badge;
		if (sku) sku.textContent = original.sku;
		if (media && media.innerHTML !== original.media) media.innerHTML = original.media;
		if (submit) submit.disabled = false;
		if (stepper) stepper.hidden = false;
		setQtyLimits(original.min ?? "1", original.max);
	};

	const show = (variation) => {
		const purchasable = variation.is_purchasable && variation.is_in_stock && variation.variation_is_visible;
		if (idInput) idInput.value = String(variation.variation_id);

		// price_html is empty when every variation costs the same.
		if (price) price.innerHTML = variation.price_html || original.price;
		if (badge) [badge.textContent, badge.className] = stockBadge(variation, unit, low);
		if (sku) sku.textContent = variation.sku || original.sku;

		const img = media && variationImage(variation);
		if (img) media.replaceChildren(img);
		else if (media && media.innerHTML !== original.media) media.innerHTML = original.media;

		if (variation.is_sold_individually === "yes") {
			if (qty) qty.value = "1";
			if (stepper) stepper.hidden = true;
		} else if (stepper) {
			stepper.hidden = false;
			setQtyLimits(variation.min_qty || 1, variation.max_qty);
		}

		if (submit) submit.disabled = !purchasable;
		setNote(
			purchasable ? variation.variation_description || "" : TEXT.unavailable,
			purchasable ? "" : "error"
		);
	};

	// Grey out options that can't combine with what's chosen in the other selects.
	const refreshOptions = () => {
		if (!variations) return;
		const current = chosen();
		selects.forEach((select) => {
			[...select.options].forEach((option) => {
				if (option.value === "") return;
				const trial = { ...current, [select.name]: option.value };
				option.disabled = !variations.some((v) => matches(v, trial));
			});
		});
	};

	const update = async () => {
		const current = chosen();
		const complete = selects.every((s) => s.value !== "");
		if (resetBtn) resetBtn.hidden = selects.every((s) => s.value === "");
		refreshOptions();

		if (!complete) {
			request++;
			restore();
			setNote("");
			return;
		}

		let variation = null;
		if (variations) {
			variation = variations.find((v) => matches(v, current)) || null;
		} else {
			const ticket = ++request;
			const body = new URLSearchParams({ ...current, product_id: form.dataset.product_id });
			try {
				const res = await fetch(form.dataset.ampVariationUrl, { method: "POST", body, credentials: "same-origin" });
				variation = (await res.json()) || null;
			} catch {
				variation = null;
			}
			if (ticket !== request) return;
		}

		if (variation) {
			show(variation);
		} else {
			restore();
			if (submit) submit.disabled = true;
			setNote(TEXT.noMatch, "error");
		}
	};

	selects.forEach((select) => select.addEventListener("change", update));

	resetBtn?.addEventListener("click", () => {
		selects.forEach((select) => {
			select.value = "";
		});
		update();
		selects[0]?.focus();
	});

	// Default attributes set in the product editor arrive pre-selected.
	update();
}

export function initVariations() {
	document.querySelectorAll(".amp-product form[data-amp-variations]").forEach(bind);
}
